import config from 'helpers/imageSizes';
import crypto from 'crypto';

interface GetImageUrlProps {
	url: string;
	focalPoint?: {
		left: number;
		top: number;
	};
	width?: number;
	height?: number;
	format?: 'webp';
}

function addUmbracoHmac(url: string, secretKey: string): string {
	const secret = Buffer.from(secretKey, 'hex');
	// The CMS emits relative media URLs (UrlModeSwitcher.Relative), so parse against a dummy
	// base — `new URL('/media/..')` would otherwise throw. ImageSharp signs over the path+query,
	// which is host-independent, so the dummy host does not affect the resulting hmac.
	const bogusOrigin = 'http://anyWebsiteJustNeedAOrigin.dk';
	const parsed = new URL(url, bogusOrigin);
	const relativeUrl = parsed.pathname + parsed.search;
	const hmac = crypto.createHmac('sha256', secret).update(relativeUrl).digest('hex').toLowerCase();

	// Append hmac at the very end
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}hmac=${hmac}`;
}

// Signs an already-complete image URL (preserving its existing query, e.g. `?rmode=max&width=500`)
// so ImageSharp accepts it when HMAC signing is enabled. Used for RTE images whose URL/resize
// params come straight from the CMS and must not be rebuilt.
export const SignImageUrl = (url: string): string => {
	if (!url) return '';

	// Umbraco 17.6+ signs RTE image sources server-side, so they arrive with a valid `hmac`
	// already. Appending a second one produces a duplicate `hmac` param that ImageSharp rejects
	// (the image fails to load). Only sign URLs that are not already signed.
	if (/[?&]hmac=/i.test(url)) return url;

	const base64EncodedSecretKey = Buffer.from(process.env.NEXT_PUBLIC_HMAC_SECRET_KEY || '', "base64").toString("hex");

	return addUmbracoHmac(url, base64EncodedSecretKey || '');
};

export const GetImageUrl = ({ url, focalPoint, width, height = 0, format }: GetImageUrlProps): string => {
	if (!url) return '';

	let imageUrl = `${url}?width=${width}`;

	if (height > 0 && focalPoint)
		imageUrl = `${imageUrl}&height=${height}&mode=crop&center=${focalPoint.top
			.toString()
			.replace(',', '.')},${focalPoint.left.toString().replace(',', '.')}`;

	if (format) imageUrl = `${imageUrl}&format=webp`;

	return SignImageUrl(imageUrl);
};

export const GetSrcSetString = ({
	url,
	focalPoint,
	format,
	aspectRatio,
}: {
	url: string;
	focalPoint: { top: number; left: number };
	format?: 'webp';
	aspectRatio?: number;
}): string => {
	const { imageSizes } = config.images;

	let string = '';

	imageSizes.map((width) => {
		let height = 0;

		if (aspectRatio) {
			height = aspectRatio * width;
		}

		return (string += `${GetImageUrl({
			url,
			focalPoint,
			height,
			width: width,
			format,
		})} ${width}w, `);
	});
	return string.substring(0, string.length - 2);
};
